import React, { useState } from 'react'

import AdminLayout from '../../../Layouts/AdminLayout/AdminLayout'
import Icon from '../../../Components/Icon/Icon'
import ContentHeader from '../../../Layouts/MainLayout/components/ContentHeader'

import './AdminDesignSystemPage.scss'

type Token = {
  name: string
  value: string
  usage: string
  swatch?: string
}

const colorTokens: Token[] = [
  { name: '--pl4-color-purple-600', value: '#663299', usage: 'Color principal Pl4nner, marca y acciones principales.', swatch: 'var(--pl4-color-purple-600)' },
  { name: '--pl4-color-violet-600', value: '#5B4BDB', usage: 'Acento moderno tomado de la propuesta Claude Design.', swatch: 'var(--pl4-color-violet-600)' },
  { name: '--pl4-color-ink-950', value: '#15172B', usage: 'Topbar oscuro SaaS y elementos de alto contraste.', swatch: 'var(--pl4-color-ink-950)' },
  { name: '--pl4-color-purple-700', value: '#5b2d88', usage: 'Hover de elementos morados.', swatch: 'var(--pl4-color-purple-700)' },
  { name: '--pl4-color-purple-800', value: '#4f2877', usage: 'Estado activo/presionado de morado.', swatch: 'var(--pl4-color-purple-800)' },
  { name: '--pl4-color-purple-050', value: '#f8f4fb', usage: 'Fondos suaves de marca.', swatch: 'var(--pl4-color-purple-050)' },
  { name: '--pl4-color-purple-border', value: '#d8c7e8', usage: 'Bordes suaves relacionados a marca.', swatch: 'var(--pl4-color-purple-border)' },
  { name: '--pl4-color-ink-900', value: '#2f2f2f', usage: 'Botones oscuros y elementos de alto contraste.', swatch: 'var(--pl4-color-ink-900)' },
  { name: '--pl4-color-ink-600', value: '#5f5f5f', usage: 'Texto e iconos secundarios.', swatch: 'var(--pl4-color-ink-600)' },
  { name: '--pl4-color-ink-200', value: '#dedbe2', usage: 'Bordes y fondos segmentados.', swatch: 'var(--pl4-color-ink-200)' },
  { name: '--pl4-color-danger', value: '#f44336', usage: 'Eliminar, errores y acciones destructivas.', swatch: 'var(--pl4-color-danger)' },
  { name: '--pl4-color-success', value: '#4b994f', usage: 'Crear, agregar, exito y estados positivos.', swatch: 'var(--pl4-color-success)' },
  { name: '--pl4-color-warning', value: '#ffc107', usage: 'Advertencias recuperables.', swatch: 'var(--pl4-color-warning)' },
  { name: '--pl4-color-info', value: '#0d6efd', usage: 'Informacion contextual.', swatch: 'var(--pl4-color-info)' },
]

const textTokens: Token[] = [
  { name: '--pl4-text-primary', value: '#2f2f2f', usage: 'Texto principal.' },
  { name: '--pl4-text-secondary', value: '#665c70', usage: 'Texto secundario y navegacion normal.' },
  { name: '--pl4-text-muted', value: '#817a88', usage: 'Ayuda, metadatos y descripciones.' },
  { name: '--pl4-text-disabled', value: '#c2c2c2', usage: 'Texto deshabilitado.' },
  { name: '--pl4-text-active', value: '#663299', usage: 'Texto activo o seleccionado.' },
  { name: '--pl4-text-hover', value: '#5b2d88', usage: 'Texto en hover.' },
]

const sizeTokens: Token[] = [
  { name: '--pl4-font-size-h1', value: '24px', usage: 'Encabezado principal.' },
  { name: '--pl4-font-size-h2', value: '20px', usage: 'Encabezado secundario.' },
  { name: '--pl4-font-size-card-title', value: '16px', usage: 'Titulos de cards.' },
  { name: '--pl4-font-size-body', value: '14px', usage: 'Texto normal.' },
  { name: '--pl4-font-size-small', value: '12px', usage: 'Texto pequeno.' },
  { name: '--pl4-font-size-helper', value: '11px', usage: 'Disclaimers y ayuda.' },
  { name: '--pl4-font-size-button', value: '13px', usage: 'Texto de botones.' },
  { name: '--pl4-font-size-tab', value: '12px', usage: 'Texto de tabs.' },
  { name: '--pl4-font-size-nav', value: '13px', usage: 'Texto de navegacion.' },
  { name: '--pl4-button-height', value: '40px', usage: 'Altura base de botones.' },
  { name: '--pl4-button-height-sm', value: '32px', usage: 'Altura de botones pequenos.' },
  { name: '--pl4-button-height-lg', value: '46px', usage: 'Altura de botones grandes.' },
  { name: '--pl4-button-radius', value: '10px', usage: 'Radio visual de botones.' },
]

const iconTokens: Token[] = [
  { name: '--pl4-icon-main-menu', value: '30px', usage: 'Iconos principales del menu.' },
  { name: '--pl4-icon-submenu', value: '18px', usage: 'Iconos de submenu.' },
  { name: '--pl4-icon-button', value: '16px', usage: 'Iconos dentro de botones.' },
  { name: '--pl4-icon-support', value: '12px', usage: 'Iconos pequenos de apoyo.' },
  { name: '--pl4-icon-tab', value: '18px', usage: 'Iconos dentro de tabs.' },
  { name: '--pl4-icon-header', value: '28px', usage: 'Iconos dentro del header.' },
]

const headerTokens: Token[] = [
  { name: '--pl4-header-height', value: '112px', usage: 'Altura recomendada del header principal.' },
  { name: '--pl4-header-padding-x', value: '24px', usage: 'Espaciado horizontal interno.' },
  { name: '--pl4-header-gap', value: '22px', usage: 'Separacion entre botones del header.' },
  { name: '--pl4-header-text-size', value: '13px', usage: 'Texto de botones del header.' },
  { name: '--pl4-header-icon-size', value: '28px', usage: 'Iconos del header.' },
  { name: '--pl4-header-bg', value: '#15172B', usage: 'Fondo del header superior.' },
  { name: '--pl4-header-border', value: '#e7e4eb', usage: 'Borde/sombra suave del header.' },
  { name: '--pl4-nav-item-radius', value: '12px', usage: 'Radio de items de navegacion principal.' },
  { name: '--pl4-nav-item-height', value: '72px', usage: 'Altura sugerida de items del menu principal.' },
  { name: '--pl4-subnav-height', value: '42px', usage: 'Altura sugerida del submenu.' },
  { name: '--pl4-tab-height', value: '38px', usage: 'Altura de tabs.' },
  { name: '--pl4-card-radius', value: '12px', usage: 'Radio base de cards.' },
]

const spacingTokens: Token[] = [
  { name: '--pl4-space-3xs', value: '2px', usage: 'Ajustes ópticos mínimos.' },
  { name: '--pl4-space-2xs', value: '4px', usage: 'Separación interna compacta.' },
  { name: '--pl4-space-sm', value: '8px', usage: 'Separación entre controles relacionados.' },
  { name: '--pl4-space-md', value: '12px', usage: 'Padding compacto y grupos de acciones.' },
  { name: '--pl4-space-lg', value: '16px', usage: 'Padding estándar de cards.' },
  { name: '--pl4-space-xl', value: '24px', usage: 'Separación entre bloques de una página.' },
  { name: '--pl4-space-2xl', value: '32px', usage: 'Separación de secciones principales.' },
  { name: '--pl4-space-4xl', value: '48px', usage: 'Respiración de layouts amplios.' },
]

const shapeTokens: Token[] = [
  { name: '--pl4-radius-sm', value: '4px', usage: 'Indicadores y elementos muy compactos.' },
  { name: '--pl4-radius-md', value: '6px', usage: 'Menús y elementos interiores.' },
  { name: '--pl4-radius-lg', value: '8px', usage: 'Cards y superficies estándar.' },
  { name: '--pl4-radius-xl', value: '12px', usage: 'Modales y superficies destacadas.' },
  { name: '--pl4-shadow-sm', value: 'Nivel 1', usage: 'Controles elevados y avatares.' },
  { name: '--pl4-shadow-md', value: 'Nivel 2', usage: 'Cards y barras flotantes.' },
  { name: '--pl4-shadow-overlay', value: 'Nivel 3', usage: 'Modales, menús y toasts.' },
]

const controlTokens: Token[] = [
  { name: '--pl4-control-height-sm', value: '32px', usage: 'Filtros y formularios densos.' },
  { name: '--pl4-control-height', value: '40px', usage: 'Altura estándar de input/select.' },
  { name: '--pl4-control-height-lg', value: '48px', usage: 'Controles principales o móviles.' },
  { name: '--pl4-control-radius', value: '8px', usage: 'Radio compartido de formularios.' },
  { name: '--pl4-control-border-focus', value: '#663299', usage: 'Borde de foco de marca.' },
  { name: '--pl4-focus-ring', value: '3px', usage: 'Indicador de teclado accesible.' },
]

const classTokens: Token[] = [
  { name: '.pl4-btn .pl4-btn-primary', value: 'Clase', usage: 'Boton principal morado.' },
  { name: '.pl4-btn .pl4-btn-secondary', value: 'Clase', usage: 'Boton secundario neutro.' },
  { name: '.pl4-btn .pl4-btn-dark', value: 'Clase', usage: 'Boton oscuro negro/plomo.' },
  { name: '.pl4-btn .pl4-btn-success', value: 'Clase', usage: 'Acciones de crear/agregar.' },
  { name: '.pl4-btn .pl4-btn-danger', value: 'Clase', usage: 'Acciones destructivas.' },
  { name: '.pl4-btn-icon', value: 'Clase', usage: 'Boton solo icono.' },
  { name: '.pl4-btn-sm / .pl4-btn-lg', value: 'Clase', usage: 'Variantes de tamano de boton.' },
  { name: '.pl4-tabs .pl4-tab', value: 'Clase', usage: 'Tabs segmentados.' },
  { name: '.pl4-card', value: 'Clase', usage: 'Contenedor/card base.' },
  { name: '.pl4-main-sidebar', value: 'Clase', usage: 'Navegación principal lateral expandible.' },
  { name: '.pl4-main-sidebar__item', value: 'Clase', usage: 'Opción activa, normal o deshabilitada del menú principal.' },
  { name: '.pl4-state-*', value: 'Clase', usage: 'Estados success/error/warning/info/loading.' },
  { name: '.pl4-field / .pl4-input / .pl4-select', value: 'Clase', usage: 'Campos y controles de formulario.' },
  { name: '.pl4-check / .pl4-radio / .pl4-switch', value: 'Clase', usage: 'Controles de selección.' },
  { name: '.pl4-badge-* / .pl4-chip', value: 'Clase', usage: 'Estados compactos y etiquetas.' },
  { name: '.pl4-alert-* / .pl4-toast', value: 'Clase', usage: 'Feedback contextual y temporal.' },
  { name: '.pl4-avatar-*', value: 'Clase', usage: 'Avatares con iniciales, imagen y presencia.' },
  { name: '.pl4-table / .pl4-table-compact', value: 'Clase', usage: 'Tablas de lectura y densidad alta.' },
  { name: '.pl4-modal-* / .pl4-menu-*', value: 'Clase', usage: 'Overlays y acciones contextuales.' },
  { name: '.pl4-dialog', value: '--pl4-dialog-width: 800px', usage: 'Modal Bootstrap con ancho configurable y desplazamiento interno. Usado en Planes y Feedback.' },
  { name: '.pl4-empty-state / .pl4-skeleton', value: 'Clase', usage: 'Vacíos y carga progresiva.' },
]

const nextItems = [
  'Date picker y selector de rangos',
  'Autocomplete y selector múltiple',
  'Carga de archivos con progreso',
  'Data table con sorting, filtros y paginación',
  'Drawer lateral y command palette',
  'Gráficos y leyendas de indicadores',
  'Editor de comentarios y menciones',
  'Auditoría WCAG con lectores de pantalla',
]

const TokenTable = ({ title, tokens }: { title: string, tokens: Token[] }) => (
  <section className="ds-section">
    <div className="ds-section-header">
      <h2>{title}</h2>
    </div>
    <div className="ds-token-table">
      {tokens.map((token) => (
        <div className="ds-token-row" key={token.name}>
          <div className="ds-token-name">
            {token.swatch && <span className="ds-swatch" style={{ background: token.swatch }} />}
            <code>{token.name}</code>
          </div>
          <div className="ds-token-value">{token.value}</div>
          <div className="ds-token-usage">{token.usage}</div>
        </div>
      ))}
    </div>
  </section>
)

const AdminDesignSystemPage = () => {
  const [pageTab, setPageTab] = useState('dashboard')
  const [filterActive, setFilterActive] = useState(false)

  return (
    <AdminLayout title="Design System" menu="design-system">
      <div className="admin-design-system-page">
        <section className="ds-hero">
          <div>
            <p className="ds-eyebrow">Glosario visual interno</p>
            <h1>Pl4nner Design System</h1>
            <p>
              Base centralizada para revisar colores, tipografía, controles, estados y componentes visuales.
              Esta página no consume API y permite validar patrones antes de aplicarlos a pantallas reales.
            </p>
          </div>
          <div className="ds-hero-card">
            <span>Color principal</span>
            <strong>#663299</strong>
            <small>Morado Pl4nner</small>
          </div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Encabezado interno compartido</h2>
            <p>ContentHeader con .pl4-page-header: proporciones de Proyectos, usadas también en Inicio. Título 24 px, acciones 36 px y texto de tabs 12 px.</p>
          </div>
          <ContentHeader
            className="pl4-page-header"
            title="Resumen de proyecto"
            subtitle="Ejemplo de revisión interna"
            buttons={[
              { label: 'Filtro', icon: <Icon name="filter" fas />, onClick: () => setFilterActive(!filterActive), active: filterActive, className: 'pl4-page-action--secondary' },
              { label: 'Exportar', icon: <Icon name="download" fas />, onClick: () => undefined, disabled: true, className: 'pl4-page-action--primary' },
            ]}
            navTabs={[
              { label: 'Dashboard', icon: <Icon name="dashboard" />, active: pageTab === 'dashboard', onClick: () => setPageTab('dashboard') },
              { label: 'Indicadores', icon: <Icon name="indicadores" />, active: pageTab === 'indicators', onClick: () => setPageTab('indicators') },
            ]}
          />
        </section>

        <TokenTable title="Colores" tokens={colorTokens} />
        <TokenTable title="Texto" tokens={textTokens} />
        <TokenTable title="Tipografia" tokens={sizeTokens} />
        <TokenTable title="Header" tokens={headerTokens} />
        <TokenTable title="Iconos" tokens={iconTokens} />
        <TokenTable title="Espaciado" tokens={spacingTokens} />
        <TokenTable title="Forma y elevación" tokens={shapeTokens} />
        <TokenTable title="Controles" tokens={controlTokens} />
        <TokenTable title="Clases reutilizables" tokens={classTokens} />

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Botones</h2>
            <p>Base reutilizable: mayor presencia, mejor jerarquia, hover claro y estados visibles sin cambiar botones existentes de la app.</p>
          </div>
          <div className="ds-button-showcase">
            <div className="ds-button-panel">
              <h3>Tipos principales</h3>
              <div className="ds-demo-grid">
                <button className="pl4-btn pl4-btn-primary">Principal</button>
                <button className="pl4-btn pl4-btn-secondary">Secundario</button>
                <button className="pl4-btn pl4-btn-dark">Oscuro</button>
                <button className="pl4-btn pl4-btn-success"><i className="fas fa-plus"></i> Agregar</button>
                <button className="pl4-btn pl4-btn-danger"><i className="fas fa-trash"></i> Eliminar</button>
                <button className="pl4-btn pl4-btn-dark pl4-btn-icon" aria-label="Configuracion"><i className="fas fa-cog"></i></button>
              </div>
            </div>

            <div className="ds-button-panel">
              <h3>Estados simulados</h3>
              <div className="ds-demo-grid">
                <button className="pl4-btn pl4-btn-primary is-hover">Hover</button>
                <button className="pl4-btn pl4-btn-primary is-active">Active</button>
                <button className="pl4-btn pl4-btn-primary is-selected">Selected</button>
                <button className="pl4-btn pl4-btn-primary" disabled>Disabled</button>
                <button className="pl4-btn pl4-btn-secondary is-hover">Hover secundario</button>
                <button className="pl4-btn pl4-btn-danger is-hover">Hover danger</button>
              </div>
            </div>

            <div className="ds-button-panel">
              <h3>Tamanos</h3>
              <div className="ds-demo-grid ds-demo-grid-align">
                <button className="pl4-btn pl4-btn-secondary pl4-btn-sm">Pequeno</button>
                <button className="pl4-btn pl4-btn-primary">Normal</button>
                <button className="pl4-btn pl4-btn-dark pl4-btn-lg">Grande</button>
              </div>
            </div>

            <div className="ds-button-panel">
              <h3>Uso recomendado</h3>
              <p>En siguientes PRs se agregaran estas clases a botones reales sin cambiar comportamiento.</p>
              <div className="ds-demo-grid">
                <button className="pl4-btn pl4-btn-primary"><i className="fas fa-save"></i> Guardar</button>
                <button className="pl4-btn pl4-btn-secondary"><i className="fas fa-times"></i> Cancelar</button>
                <button className="pl4-btn pl4-btn-danger"><i className="fas fa-trash"></i> Eliminar</button>
              </div>
            </div>
          </div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Tabs</h2>
            <p>Patron recomendado para tabs compactos y segmentados.</p>
          </div>
          <div className="pl4-tabs">
            <button className="pl4-tab is-active"><i className="fas fa-building"></i> Activo</button>
            <button className="pl4-tab"><i className="fas fa-hashtag"></i> Inactivo</button>
            <button className="pl4-tab is-hover"><i className="fas fa-users"></i> Hover</button>
            <button className="pl4-tab is-disabled" disabled><i className="fas fa-ban"></i> Disabled</button>
          </div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Navegacion principal</h2>
            <p>La barra superior concentra el contexto y las acciones; los módulos viven en el menú lateral.</p>
          </div>
          <div className="ds-shell-preview">
            <div className="ds-shell-logo"><span>PL4</span>NNER</div>
            <div className="ds-shell-project"><i /> Costa Morena 1 <Icon name="chevron-down" fas /></div>
            <div className="ds-shell-spacer" />
            <button aria-label="Notificaciones" className="ds-shell-action" type="button"><Icon name="bell" far /></button>
            <button aria-label="Chat" className="ds-shell-action" type="button"><Icon name="comment-alt" far /></button>
            <div className="ds-shell-avatar">RM</div>
          </div>
          <div className="ds-layout-preview">
            <aside className="ds-sidebar-preview">
              <div className="ds-sidebar-item is-active"><Icon name="folder" far /><span>Proyectos</span></div>
              <div className="ds-sidebar-item"><Icon name="chart-pie" fas /><span>Inicio</span></div>
              <div className="ds-sidebar-item"><Icon name="users" fas /><span>Equipos</span></div>
              <div className="ds-sidebar-item"><Icon name="calendar-alt" far /><span>Programación</span></div>
              <div className="ds-sidebar-item"><Icon name="tasks" fas /><span>Tareas</span></div>
              <div className="ds-sidebar-item is-disabled"><Icon name="cube" fas /><span>BIM</span></div>
              <div className="ds-sidebar-spacer" />
              <div className="ds-sidebar-item"><Icon name="cog" fas /><span>Configuración</span></div>
            </aside>
            <div className="ds-layout-content">
              <span>Contenido del módulo</span>
              <strong>Proyectos activos</strong>
              <p>Los tabs internos se muestran aquí, dentro de la página seleccionada.</p>
              <div className="pl4-tabs">
                <button className="pl4-tab is-active">Dashboard</button>
                <button className="pl4-tab">Indicadores</button>
              </div>
            </div>
          </div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Estados</h2>
            <p>Referencia visual para feedback y estados de UI.</p>
          </div>
          <div className="ds-demo-grid">
            <span className="pl4-state pl4-state-info">Normal</span>
            <span className="pl4-state pl4-state-info">Focus</span>
            <span className="pl4-state pl4-state-success">Success</span>
            <span className="pl4-state pl4-state-error">Error</span>
            <span className="pl4-state pl4-state-warning">Warning</span>
            <span className="pl4-state pl4-state-loading">Loading</span>
          </div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Cards y acciones</h2>
          </div>
          <div className="ds-card-grid">
            <div className="pl4-card">
              <h3 className="pl4-card-title">Card base</h3>
              <p className="pl4-body">Superficie blanca, borde suave y radio moderado.</p>
              <p className="pl4-helper">Clase: .pl4-card</p>
            </div>
            <div className="pl4-card">
              <h3 className="pl4-card-title">Acciones</h3>
              <div className="ds-inline-actions">
                <button className="pl4-btn pl4-btn-success">Crear</button>
                <button className="pl4-btn pl4-btn-secondary">Editar</button>
                <button className="pl4-btn pl4-btn-danger">Eliminar</button>
              </div>
            </div>
            <div className="ds-project-card-preview">
              <button className="ds-card-menu" aria-label="Mas opciones"><i className="fas fa-ellipsis-v"></i></button>
              <div className="ds-project-icon"><i className="fas fa-folder"></i></div>
              <strong>Costa Morena 1</strong>
              <span>Card de proyecto</span>
            </div>
          </div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Formularios</h2>
            <p>Controles compactos con estados normal, focus, error y disabled.</p>
          </div>
          <div className="ds-form-grid">
            <div className="pl4-field">
              <label className="pl4-field__label" htmlFor="ds-name">Nombre del proyecto <span className="pl4-field__required">*</span></label>
              <input className="pl4-input" id="ds-name" placeholder="Ej. Costa Morena" />
              <p className="pl4-field__hint">Nombre visible para todos los miembros.</p>
            </div>
            <div className="pl4-field">
              <label className="pl4-field__label" htmlFor="ds-status">Estado</label>
              <select className="pl4-select" id="ds-status" defaultValue="active">
                <option value="active">En curso</option>
                <option value="planning">Planificación</option>
                <option value="complete">Completado</option>
              </select>
            </div>
            <div className="pl4-field">
              <label className="pl4-field__label" htmlFor="ds-code">Código</label>
              <input aria-invalid="true" className="pl4-input" defaultValue="PR-001" id="ds-code" />
              <p className="pl4-field__error">Este código ya está en uso.</p>
            </div>
            <div className="pl4-field">
              <label className="pl4-field__label" htmlFor="ds-disabled">Responsable</label>
              <input className="pl4-input" disabled id="ds-disabled" value="Roger Montenegro" readOnly />
            </div>
            <div className="pl4-field ds-form-span">
              <label className="pl4-field__label" htmlFor="ds-description">Descripción</label>
              <textarea className="pl4-textarea" id="ds-description" placeholder="Agrega información útil para el equipo" />
            </div>
            <div className="pl4-field ds-form-span">
              <span className="pl4-field__label">Selección</span>
              <div className="ds-selection-row">
                <label className="pl4-check"><input defaultChecked type="checkbox" /> Mostrar avance</label>
                <label className="pl4-radio"><input defaultChecked name="density" type="radio" /> Cómodo</label>
                <label className="pl4-radio"><input name="density" type="radio" /> Compacto</label>
                <label className="pl4-switch"><input defaultChecked type="checkbox" /> Notificaciones</label>
              </div>
            </div>
          </div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Badges, chips y progreso</h2>
            <p>Indicadores breves; no reemplazan mensajes completos ni botones.</p>
          </div>
          <div className="ds-demo-grid">
            <span className="pl4-badge pl4-badge-primary">Nuevo</span>
            <span className="pl4-badge pl4-badge-neutral">Borrador</span>
            <span className="pl4-badge pl4-badge-success">Completado</span>
            <span className="pl4-badge pl4-badge-danger">Atrasado</span>
            <span className="pl4-badge pl4-badge-warning">Planificación</span>
            <span className="pl4-badge pl4-badge-info">En curso</span>
            <span className="pl4-chip"><Icon name="user" far /> Roger</span>
            <span className="pl4-chip"><Icon name="calendar" far /> Semana 24</span>
          </div>
          <div className="ds-progress-demo">
            <span>Avance del proyecto</span>
            <strong>72%</strong>
            <div className="pl4-progress"><span className="pl4-progress__bar" style={{ width: '72%' }} /></div>
          </div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Avatares y presencia</h2>
            <p>La inicial es el fallback cuando no existe una imagen válida.</p>
          </div>
          <div className="ds-avatar-row">
            <span className="pl4-avatar pl4-avatar-sm">RM</span>
            <span className="pl4-avatar-wrap"><span className="pl4-avatar">RM</span><i className="pl4-avatar-status" /></span>
            <span className="pl4-avatar pl4-avatar-lg">CM</span>
            <div className="ds-avatar-identity"><strong>Roger Montenegro</strong><span>Administrador</span></div>
          </div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Feedback</h2>
            <p>Alertas persistentes y toast temporal con la misma jerarquía visual.</p>
          </div>
          <div className="ds-feedback-grid">
            <div className="pl4-alert pl4-alert-success"><Icon name="check-circle" far /><div><strong>Cambios guardados</strong>El proyecto se actualizó correctamente.</div></div>
            <div className="pl4-alert pl4-alert-danger"><Icon name="times-circle" far /><div><strong>No se pudo guardar</strong>Revisa los campos marcados e inténtalo nuevamente.</div></div>
            <div className="pl4-alert pl4-alert-warning"><Icon name="exclamation-triangle" fas /><div><strong>Requiere atención</strong>Hay tres tareas sin responsable.</div></div>
            <div className="pl4-alert pl4-alert-info"><Icon name="info-circle" fas /><div><strong>Información</strong>El avance se actualiza cada semana.</div></div>
          </div>
          <div className="pl4-toast ds-toast-demo"><Icon name="bell" far /><div><strong>Nueva notificación</strong>Se agregó un comentario a Plan semanal.</div></div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Tabla</h2>
            <p>Tabla compacta para información operativa y acciones repetidas.</p>
          </div>
          <div className="pl4-table-wrap">
            <table className="pl4-table">
              <thead><tr><th>Tarea</th><th>Responsable</th><th>Estado</th><th>Avance</th><th aria-label="Acciones" /></tr></thead>
              <tbody>
                <tr><td>Excavación general</td><td>Roger Montenegro</td><td><span className="pl4-badge pl4-badge-info">En curso</span></td><td>72%</td><td><button aria-label="Más opciones" className="pl4-btn pl4-btn-secondary pl4-btn-icon pl4-btn-sm" type="button"><Icon name="ellipsis-v" fas /></button></td></tr>
                <tr><td>Retiro de material</td><td>Equipo civil</td><td><span className="pl4-badge pl4-badge-warning">Planificación</span></td><td>24%</td><td><button aria-label="Más opciones" className="pl4-btn pl4-btn-secondary pl4-btn-icon pl4-btn-sm" type="button"><Icon name="ellipsis-v" fas /></button></td></tr>
                <tr><td>Hormigonado</td><td>Sin asignar</td><td><span className="pl4-badge pl4-badge-danger">Atrasado</span></td><td>10%</td><td><button aria-label="Más opciones" className="pl4-btn pl4-btn-secondary pl4-btn-icon pl4-btn-sm" type="button"><Icon name="ellipsis-v" fas /></button></td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Navegación contextual y overlays</h2>
            <p>Breadcrumb, tooltip, menú y modal de referencia.</p>
          </div>
          <ol className="pl4-breadcrumb">
            <li><a href="#design-system">Proyectos</a></li><li><Icon name="chevron-right" fas /></li><li><a href="#design-system">Costa Morena</a></li><li><Icon name="chevron-right" fas /></li><li aria-current="page">Programación</li>
          </ol>
          <div className="ds-overlay-actions">
            <span className="pl4-tooltip-trigger" data-tooltip="Filtrar resultados"><button className="pl4-btn pl4-btn-secondary pl4-btn-icon" type="button"><Icon name="filter" fas /></button></span>
            <details className="pl4-menu">
              <summary className="pl4-btn pl4-btn-secondary">Acciones <Icon name="chevron-down" fas /></summary>
              <div className="pl4-menu__surface">
                <button className="pl4-menu__item" type="button"><Icon name="edit" far /> Editar</button>
                <button className="pl4-menu__item" type="button"><Icon name="copy" far /> Duplicar</button>
                <button className="pl4-menu__item is-danger" type="button"><Icon name="trash-alt" far /> Eliminar</button>
              </div>
            </details>
          </div>
          <div className="pl4-modal-backdrop ds-modal-preview">
            <div aria-labelledby="ds-modal-title" aria-modal="true" className="pl4-modal" role="dialog">
              <div className="pl4-modal__header"><h3 id="ds-modal-title">Crear proyecto</h3><button aria-label="Cerrar" className="pl4-btn pl4-btn-secondary pl4-btn-icon pl4-btn-sm" type="button"><Icon name="times" fas /></button></div>
              <div className="pl4-modal__body"><div className="pl4-field"><label className="pl4-field__label" htmlFor="ds-modal-name">Nombre</label><input className="pl4-input" id="ds-modal-name" placeholder="Nombre del proyecto" /></div></div>
              <div className="pl4-modal__footer"><button className="pl4-btn pl4-btn-secondary" type="button">Cancelar</button><button className="pl4-btn pl4-btn-primary" type="button">Crear proyecto</button></div>
            </div>
          </div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Vacío y carga</h2>
            <p>Estados que mantienen estable el layout durante la carga o ausencia de datos.</p>
          </div>
          <div className="ds-state-grid">
            <div className="pl4-empty-state"><Icon name="inbox" fas /><h3>Aún no hay tareas</h3><p>Crea una tarea para comenzar a organizar el trabajo del equipo.</p><button className="pl4-btn pl4-btn-primary pl4-btn-sm" type="button"><Icon name="plus" fas /> Nueva tarea</button></div>
            <div className="ds-skeleton-card"><span className="pl4-skeleton ds-skeleton-avatar" /><span className="pl4-skeleton ds-skeleton-title" /><span className="pl4-skeleton ds-skeleton-line" /><span className="pl4-skeleton ds-skeleton-line is-short" /></div>
          </div>
        </section>

        <section className="ds-section">
          <div className="ds-section-header">
            <h2>Siguiente etapa</h2>
            <p>Componentes complejos que necesitan comportamiento y pruebas propias.</p>
          </div>
          <div className="ds-missing-grid">
            {nextItems.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </section>
      </div>
    </AdminLayout>
  )
}

export default AdminDesignSystemPage
